import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Image as ImageIcon, Upload, Trash2, Send, ExternalLink, Link2, Check, X, Pencil, Loader2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { API_URL, BACKEND_URL } from '../../utils/api';
import { getErrorMessage } from '../../utils/errors';

const ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
const MAX_BYTES = 20 * 1024 * 1024;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const formatSize = (bytes = 0) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const fileUrl = (url = '') => (url.startsWith('http') ? url : `${BACKEND_URL}${url}`);

/** Split free text ("a@b.com, 845-555-1234\n...") into emails and phone numbers. */
export const parseRecipients = (text) => {
  const emails = [];
  const phones = [];
  const invalid = [];
  text
    .split(/[\s,;]+/)
    .map((t) => t.trim())
    .filter(Boolean)
    .forEach((token) => {
      if (token.includes('@')) {
        (EMAIL_RE.test(token) ? emails : invalid).push(token);
      } else if (token.replace(/\D/g, '').length >= 10) {
        phones.push(token);
      } else {
        invalid.push(token);
      }
    });
  return { emails, phones, invalid };
};

/**
 * Manage and send a category's catalogs (PDF / JPG / PNG).
 * Email sends the files as attachments; SMS sends images as MMS plus links.
 */
export default function CategoryCatalogDialog({ category, open, onOpenChange, token, onChanged }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [selected, setSelected] = useState([]);
  const [recipients, setRecipients] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [genPdf, setGenPdf] = useState(true);
  const [genJpg, setGenJpg] = useState(true);
  const [genReplace, setGenReplace] = useState(true);
  const [generating, setGenerating] = useState(false);
  const inputRef = useRef(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const publicLink = category ? `${window.location.origin}/catalog/${category.id}` : '';

  useEffect(() => {
    if (!open || !category) return;
    const list = category.catalog_files || [];
    setFiles(list);
    setSelected(list.map((f) => f.id));
    setRecipients('');
    setMessage('');
    setLastResult(null);
    setRenamingId(null);
  }, [open, category]);

  const updateFiles = (next) => {
    setFiles(next);
    onChanged?.(category.id, next);
  };

  const uploadFiles = async (fileList) => {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    setUploading(true);
    let next = [...files];
    for (const file of picked) {
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name} is larger than 20 MB`);
        continue;
      }
      const form = new FormData();
      form.append('file', file);
      form.append('name', file.name.replace(/\.[^.]+$/, ''));
      try {
        const { data } = await axios.post(`${API_URL}/admin/categories/${category.id}/catalogs`, form, auth);
        next = [...next, data];
        setSelected((s) => [...s, data.id]);
        toast.success(`Uploaded ${data.name}`);
      } catch (error) {
        toast.error(getErrorMessage(error, `Could not upload ${file.name}`));
      }
    }
    updateFiles(next);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeFile = async (file) => {
    if (!window.confirm(`Delete "${file.name}"? Customers will no longer receive it.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/categories/${category.id}/catalogs/${file.id}`, auth);
      updateFiles(files.filter((f) => f.id !== file.id));
      setSelected((s) => s.filter((id) => id !== file.id));
      toast.success('Catalog deleted');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not delete the catalog'));
    }
  };

  const saveRename = async (file) => {
    const name = renameValue.trim();
    if (!name || name === file.name) {
      setRenamingId(null);
      return;
    }
    try {
      const { data } = await axios.patch(
        `${API_URL}/admin/categories/${category.id}/catalogs/${file.id}`, { name }, auth,
      );
      updateFiles(files.map((f) => (f.id === file.id ? data : f)));
      setRenamingId(null);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not rename the catalog'));
    }
  };

  /** Build the catalog from this category's live designs (server renders every design with its code). */
  const generate = async () => {
    const formats = [genPdf && 'pdf', genJpg && 'jpg'].filter(Boolean);
    if (!formats.length) {
      toast.error('Choose PDF and/or JPG');
      return;
    }
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/admin/categories/${category.id}/catalogs/generate`,
        { formats, replace: genReplace },
        { ...auth, timeout: 180000 },
      );
      updateFiles(data.catalogs || []);
      setSelected((data.created || []).map((f) => f.id));
      toast.success(`Catalog made from ${data.designs} design${data.designs === 1 ? '' : 's'} · ${data.pages} page${data.pages === 1 ? '' : 's'}`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not generate the catalog'));
    } finally {
      setGenerating(false);
    }
  };

  const { emails, phones, invalid } = parseRecipients(recipients);
  const canSend = selected.length > 0 && (emails.length + phones.length) > 0 && invalid.length === 0 && !sending;

  const send = async (e) => {
    e.preventDefault();
    if (!canSend) return;
    setSending(true);
    setLastResult(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/admin/categories/${category.id}/catalogs/send`,
        { emails, phones, file_ids: selected, message },
        auth,
      );
      setLastResult(data);
      if (data.failed?.length) {
        toast.warning(`Sent to ${data.sent.length}, failed for ${data.failed.length}`);
      } else {
        toast.success(`Catalog sent to ${data.sent.length} recipient${data.sent.length === 1 ? '' : 's'}`);
        setRecipients('');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not send the catalog'));
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicLink);
      toast.success('Catalog link copied');
    } catch {
      toast.error('Could not copy the link');
    }
  };

  if (!category) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{category.name} catalog</DialogTitle>
          <DialogDescription>
            Upload PDF or JPG catalogs for this category, then send them to customers by email or text message.
            Phone callers can also request them from the IVR catalog menu.
          </DialogDescription>
        </DialogHeader>

        {/* Generate automatically */}
        <section className="min-w-0 space-y-3 rounded-lg border border-gold/40 bg-gold/5 p-3 sm:p-4" aria-labelledby="catalog-generate-heading">
          <h3 id="catalog-generate-heading" className="font-semibold text-ink">Generate from the designs</h3>
          <p className="text-xs text-ink/70">
            Builds the catalog automatically from every active design in {category.name}: each design is drawn with its
            wording, and labelled with its code (what phone callers type) and price — 6 per page. Run it again after
            adding or changing designs.
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
            <label className="flex items-center gap-2">
              <Checkbox checked={genPdf} onCheckedChange={(v) => setGenPdf(Boolean(v))} /> PDF (all pages, for email)
            </label>
            <label className="flex items-center gap-2">
              <Checkbox checked={genJpg} onCheckedChange={(v) => setGenJpg(Boolean(v))} /> JPG pages (for text messages)
            </label>
            <label className="flex items-center gap-2">
              <Checkbox checked={genReplace} onCheckedChange={(v) => setGenReplace(Boolean(v))} /> Replace the previous generated catalog
            </label>
          </div>
          <Button type="button" onClick={generate} disabled={generating} className="bg-ink text-paper hover:bg-ink/90" data-testid="catalog-generate">
            {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
            {generating ? 'Drawing the designs… (up to a minute)' : 'Generate catalog'}
          </Button>
        </section>

        {/* Files */}
        <section className="min-w-0 space-y-3" aria-labelledby="catalog-files-heading">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 id="catalog-files-heading" className="font-semibold text-ink">Files</h3>
            {files.length > 0 && (
              <Button type="button" variant="ghost" size="sm" onClick={copyLink} className="text-ink/70">
                <Link2 className="h-4 w-4 mr-1" /> Copy public link
              </Button>
            )}
          </div>

          {files.length === 0 ? (
            <p className="text-sm text-ink/60 rounded-lg border border-dashed border-border p-4 text-center">
              No catalogs yet. Generate one above, or upload a PDF or JPG below.
            </p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {files.map((file) => {
                const isPdf = file.content_type === 'application/pdf';
                const Icon = isPdf ? FileText : ImageIcon;
                return (
                  <li key={file.id} className="flex min-w-0 items-center gap-2 sm:gap-3 p-2 sm:p-3">
                    <Checkbox
                      checked={selected.includes(file.id)}
                      onCheckedChange={(v) =>
                        setSelected((s) => (v ? [...s, file.id] : s.filter((id) => id !== file.id)))}
                      aria-label={`Include ${file.name} when sending`}
                    />
                    {isPdf ? (
                      <div className="h-10 w-10 sm:h-12 sm:w-12 shrink-0 rounded bg-red-50 flex items-center justify-center">
                        <Icon className="h-6 w-6 text-red-600" aria-hidden="true" />
                      </div>
                    ) : (
                      <img src={fileUrl(file.url)} alt="" className="h-10 w-10 sm:h-12 sm:w-12 shrink-0 rounded object-cover border border-border" />
                    )}
                    <div className="min-w-0 flex-1">
                      {renamingId === file.id ? (
                        <div className="flex items-center gap-1">
                          <Input
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { e.preventDefault(); saveRename(file); }
                              if (e.key === 'Escape') setRenamingId(null);
                            }}
                            className="h-8"
                            aria-label="Catalog name"
                            autoFocus
                          />
                          <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => saveRename(file)} aria-label="Save name">
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => setRenamingId(null)} aria-label="Cancel rename">
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <p className="font-medium text-ink truncate">{file.name}</p>
                      )}
                      <p className="text-xs text-ink/50">
                        {isPdf ? 'PDF' : 'Image'} · {formatSize(file.size)}
                        {file.auto ? <span className="ml-2 rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-semibold text-ink">Auto</span> : null}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center">
                      <Button asChild size="icon" variant="ghost" className="h-8 w-8">
                        <a href={fileUrl(file.url)} target="_blank" rel="noopener noreferrer" aria-label={`Open ${file.name}`}>
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                      <Button
                        type="button" size="icon" variant="ghost" className="h-8 w-8"
                        onClick={() => { setRenamingId(file.id); setRenameValue(file.name); }}
                        aria-label={`Rename ${file.name}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive"
                        onClick={() => removeFile(file)} aria-label={`Delete ${file.name}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <label
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
            className={`flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed p-5 text-center transition-colors focus-within:ring-2 focus-within:ring-gold ${
              dragOver ? 'border-gold bg-gold/5' : 'border-border hover:border-gold/60'
            }`}
          >
            {uploading ? (
              <Loader2 className="h-6 w-6 animate-spin text-gold" aria-hidden="true" />
            ) : (
              <Upload className="h-6 w-6 text-ink/50" aria-hidden="true" />
            )}
            <span className="text-sm font-medium text-ink">
              {uploading ? 'Uploading…' : 'Click to upload or drag files here'}
            </span>
            <span className="text-xs text-ink/50">PDF, JPG or PNG · up to 20 MB each</span>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              multiple
              className="sr-only"
              disabled={uploading}
              onChange={(e) => uploadFiles(e.target.files)}
            />
          </label>
        </section>

        {/* Send */}
        {files.length > 0 && (
          <form onSubmit={send} className="min-w-0 space-y-3 border-t border-border pt-5" aria-labelledby="catalog-send-heading">
            <h3 id="catalog-send-heading" className="font-semibold text-ink">Send to customers</h3>
            <div>
              <Label htmlFor="catalog-recipients">Email addresses or phone numbers</Label>
              <Textarea
                id="catalog-recipients"
                value={recipients}
                onChange={(e) => setRecipients(e.target.value)}
                placeholder={'customer@example.com\n845-555-1234'}
                rows={3}
                className="mt-2"
                aria-describedby="catalog-recipients-help"
              />
              <p id="catalog-recipients-help" className="mt-1 text-xs text-ink/60">
                Separate with commas or new lines.
                {(emails.length > 0 || phones.length > 0) && (
                  <> Will send {emails.length > 0 && `${emails.length} email${emails.length === 1 ? '' : 's'}`}
                    {emails.length > 0 && phones.length > 0 && ' and '}
                    {phones.length > 0 && `${phones.length} text message${phones.length === 1 ? '' : 's'}`}.</>
                )}
              </p>
              {invalid.length > 0 && (
                <p className="mt-1 text-xs text-destructive" role="alert">
                  Not a valid email or phone number: {invalid.join(', ')}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="catalog-message">Personal note (optional)</Label>
              <Textarea
                id="catalog-message"
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, 500))}
                placeholder="Mazel tov! Here is our catalog…"
                rows={2}
                className="mt-2"
              />
            </div>
            <p className="text-xs text-ink/60">
              Email includes the selected files as attachments. Text messages include images as picture messages
              and links for PDFs.
            </p>
            {selected.length === 0 && (
              <p className="text-xs text-destructive">Select at least one file to send.</p>
            )}
            <Button type="submit" disabled={!canSend} className="w-full bg-ink text-paper hover:bg-ink/90">
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              {sending ? 'Sending…' : `Send ${selected.length} file${selected.length === 1 ? '' : 's'}`}
            </Button>

            {lastResult?.failed?.length > 0 && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm" role="status">
                <p className="font-medium text-ink mb-1">Some messages were not sent:</p>
                <ul className="list-disc pl-5 text-ink/70">
                  {lastResult.failed.map((f) => (
                    <li key={`${f.channel}-${f.to}`}>{f.to}: {f.error}</li>
                  ))}
                </ul>
              </div>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
